import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Container, Select, MenuItem, FormControl, InputLabel, Grid, Card, CardContent, CardMedia, CircularProgress, Button, Divider, Chip, Avatar, Stack } from '@mui/material';
import AnalyticsTabs from './AnalyticsTabs';
import { useWorkspaces } from 'contexts/WorkspacesContext';
import { useParams } from 'react-router-dom';
import { useGetProjectScansQuery } from 'api/project/queries/useGetProjectScansQuery';
import { useGetProjectQuery } from 'api/project/queries/useGetProjectQuery';
import { useGetScanAnalyticsQuery } from 'api/scan/queries/useGetScanAnalyticsQuery';
import { useGetScanQuery } from 'api/scan/queries/useGetScanQuery';
import { ScanDetails } from 'types/scan';

// Extended interfaces with additional properties
interface ExtendedAnalytics {
  analytics: Analytics;
  created_at?: string;
  updated_at?: string;
}

interface ExtendedScanDetails extends ScanDetails {
  type?: string;
}
import { getHTTPUrlFromS3Path } from 'utils/outputs';
import moment from 'moment';

interface Analytics {
  deviations: {
    mode: string;
    images: string[];
    floorplan_id: string;
  }[];
  visual_defects: {
    type: string;
    image: string;
    caption: string;
    bbox: {
      '2d': { x1: number; y1: number; x2: number; y2: number };
      '3d': {
        x_min: number; y_min: number; z_min: number;
        x_max: number; y_max: number; z_max: number;
      };
    };
  }[];
  progress: {
    [key: string]: {
      stage: string;
      pairs: {
        image1: string;
        image2: string;
        caption: string;
      }[];
      summary: {
        text: string;
        floorplan_id: string;
      }[];
      extensions: Record<string, unknown>;
    };
  };
  detections: {
    category: string;
    image: string;
    caption: string;
    bbox: {
      '2d': { x1: number; y1: number; x2: number; y2: number };
      '3d': {
        x_min: number; y_min: number; z_min: number;
        x_max: number; y_max: number; z_max: number;
      };
    };
  }[];
  extensions: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

const ProjectAnalytics: React.FC = () => {
  const [selectedScan, setSelectedScan] = useState<string>('');
  const { activeWorkspace } = useWorkspaces();
  const { projectSlug = "", workspaceSlug = "" } = useParams<{ projectSlug: string; workspaceSlug: string }>();
  
  const { data: project } = useGetProjectQuery(projectSlug, workspaceSlug);
  const { 
    data: scansData,
    isLoading: isLoadingScans,
    isError: isErrorScans,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useGetProjectScansQuery(project?.id || "");

  const {
    data: analyticsData,
    isLoading: isLoadingAnalytics,
    isError: isErrorAnalytics
  } = useGetScanAnalyticsQuery(selectedScan) as { data: ExtendedAnalytics | undefined, isLoading: boolean, isError: boolean };

  // Log analytics data when it changes
  useEffect(() => {
    if (analyticsData) {
      console.log('Scan Analytics:', analyticsData);
    }
  }, [analyticsData]);

  // Flatten the pages of scan data
  const scans = useMemo(() => {
    return scansData?.pages?.flatMap(page => page.items) || [];
  }, [scansData]);
  
  // Set the first scan as selected when data loads
  useEffect(() => {
    if (scans.length > 0 && !selectedScan) {
      setSelectedScan(scans[0].id);
    }
  }, [scans, selectedScan]);

  if (activeWorkspace && !activeWorkspace.featureFlags?.includes("enable-project-analytics")) {
    return (
      <Box sx={{ width: '100%', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', p: 4, borderRadius: 2 }}>
            <Typography variant="h1" component="h1" sx={{ mb: 3, color: 'primary.main' }}>
              Project Analytics
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              Analytics is not currently enabled for this workspace.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  if (isLoadingScans) {
    return (
      <Box sx={{ width: '100%', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isErrorScans) {
    return (
      <Box sx={{ width: '100%', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">Error loading scans. Please try again later.</Typography>
      </Box>
    );
  }

  const selectedScanData = scans.find(scan => scan.id === selectedScan);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', py: 3 }}>
      <Container maxWidth={false}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h1" component="h1">
            Project Analytics
          </Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="scan-select-label">Select Scan</InputLabel>
            <Select
              labelId="scan-select-label"
              value={selectedScan}
              label="Select Scan"
              onChange={(e) => setSelectedScan(e.target.value)}
            >
              {scans.map((scan: ScanDetails) => (
                <MenuItem key={scan.id} value={scan.id}>
                  {scan.description ? `${scan.description} - ` : ''}
                  {new Date(scan.scanDate || scan.createdAt).toLocaleDateString()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {isLoadingAnalytics ? (
          <Box sx={{ width: '100%', minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : isErrorAnalytics ? (
          <Box 
            sx={{ 
              width: '100%', 
              minHeight: '50vh', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Typography variant="h6" color="error">
              Error loading analytics data
            </Typography>
            <Typography variant="body1" color="text.secondary">
              There was a problem fetching analytics for this scan. Please try selecting another scan or try again later.
            </Typography>
          </Box>
        ) : analyticsData?.analytics ? (
          <>
            <AnalyticsTabs
              analytics={analyticsData.analytics}
              scans={scans}
              selectedScan={selectedScan}
              analyticsCreatedAt={analyticsData.created_at}
              selectedScanData={selectedScanData}
            />
            {/* Show message if no sections have data */}
            {!analyticsData.analytics.deviations?.length && 
             !analyticsData.analytics.visual_defects?.length && 
             !Object.keys(analyticsData.analytics.progress || {}).length && 
             !analyticsData.analytics.detections?.length && (
              <Box 
                sx={{ 
                  width: '100%', 
                  minHeight: '50vh', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No analytics data found
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  This scan has no deviations, visual defects, progress, or detections to display.
                </Typography>
              </Box>
            )}
          </>
        ) : selectedScan ? (
          <Box 
            sx={{ 
              width: '100%', 
              minHeight: '50vh', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No analytics found for this scan
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Analytics data may still be processing or may not be available for this scan.
            </Typography>
          </Box>
        ) : null}
      </Container>
    </Box>
  );
};

export default ProjectAnalytics;